import React, { CSSProperties, FC } from 'react';
import { Settings } from '@/Settings';
import { M24MediaModel } from '@/declarations/models/M24MediaModel';
import { ExternalMediaModel, isDiMuMediaModel } from '@/declarations/models/ExternalMediaModel';
import { DamsMediaItem } from '@/declarations/models/DamsMediaResponse';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import { isDamsMediaItem, isM24MediaModel } from '@/utils/typeChecks';
import { calculateObjectPosition } from './Image';

export interface ImagePreviewProps extends ChildrenProp {
  mediaObject?: M24MediaModel | ExternalMediaModel | DamsMediaItem;
  skin?: string;
  imgClassName?: string;
  id?: string;
  imgStyle?: CSSProperties;
  opacity?: string;
  effect?: string;
  disableFocusPoint?: boolean;
  dimension?: string;
}

export const ImagePreview: FC<ImagePreviewProps> = ({
  children,
  mediaObject,
  imgClassName,
  skin,
  id,
  imgStyle,
  opacity,
  effect,
  disableFocusPoint = false,
  dimension,
}) => {
  if (!mediaObject) {
    return null;
  }

  const damsEmsDomain = Settings.viteEnvironmentIsProduction ? 'ems.dimu.org' : 'ems-test.dimu.org';

  let altText = '';

  if (isM24MediaModel(mediaObject)) {
    altText = mediaObject.content?.alttext || '';
  } else if (isDamsMediaItem(mediaObject)) {
    altText = mediaObject.title || mediaObject.description || '';
  } else if (isDiMuMediaModel(mediaObject)) {
    altText =
      mediaObject.local?.title ||
      mediaObject.item?.['artifact.ingress.title'] ||
      mediaObject.id ||
      mediaObject.dimuId ||
      mediaObject.dmsId ||
      mediaObject.objectId ||
      '';
  }

  let imgUrl = '';

  if (isDamsMediaItem(mediaObject)) {
    imgUrl = mediaObject.m24?.ems_id ? `https://${damsEmsDomain}/image/${mediaObject.m24.ems_id}` : '';
  } else if ('url' in mediaObject && mediaObject.url) {
    imgUrl = mediaObject.url.replace('mm.', 'ems.');
  }

  if (
    !isDamsMediaItem(mediaObject) &&
    'type' in mediaObject &&
    mediaObject.type === 'external_url' &&
    mediaObject.local?.youtubeId
  ) {
    imgUrl = `https://img.youtube.com/vi/${mediaObject.local.youtubeId}/hqdefault.jpg`;
  }

  if (
    !isDamsMediaItem(mediaObject) &&
    'type' in mediaObject &&
    mediaObject.type === 'external_url' &&
    mediaObject.local?.vimeoId
  ) {
    imgUrl = `https://i.vimeocdn.com/video/${mediaObject.local.vimeoId}_720x400`;
  }

  let mime: string | undefined;

  if (isDamsMediaItem(mediaObject)) {
    mime = mediaObject.mime_type;
  } else if ('mimetype' in mediaObject) {
    mime = mediaObject.mimetype;
  }

  let imgMimeType = '';
  if (mime?.includes('png')) {
    imgMimeType = `?mediaType=${mime}`;
  }

  let dimensionsQuery = '';
  if (dimension) {
    dimensionsQuery = `${imgMimeType ? '&' : '?'}dimension=${dimension}`;
  }
  const objectPosition = disableFocusPoint
    ? 'center'
    : calculateObjectPosition(
        'local' in mediaObject ? mediaObject.local?.x : undefined,
        'local' in mediaObject ? mediaObject.local?.y : undefined,
      );

  return (
    <>
      <div id={id} className={`module__media ${skin || ''}`} style={{ opacity: opacity || '1' }}>
        <img
          style={{
            ...imgStyle,
            objectPosition,
          }}
          src={`${imgUrl}${imgMimeType}${dimensionsQuery}`}
          alt={altText}
          className={`${imgClassName || ''} ${effect || ''}`}
        />
      </div>
      {children}
    </>
  );
};

export default ImagePreview;
